/**
 * useScrapeMatchSummaries
 *
 * Asks the server to fill in `matchSummary` (counts by mailing reason — see
 * functions/scrapes/matchSummary.js) for any COMPLETED scrape in view that has
 * none: every scrape that finished before summaries existed. New scrapes get
 * one automatically when they complete. The summary then arrives through the
 * scrape list's own listener.
 *
 * ⚠️ And for a scrape that STOPPED EARLY (failed or cancelled) with saved homes
 * (12 Oct 2026). Nothing counted one until then: the count is written when a
 * scrape completes, and this asked only for completed ones, so a stopped
 * scrape's homes were in no mailable figure. Which scrapes to ask about is the
 * page's rule, `needsCount` in utils/groupTargets.js.
 *
 * WHAT THE FUNCTION DOES, read whole (a stand-in in a test hides this):
 *  - it counts and stores ONLY A SCRAPE THAT HAS FINISHED (completed, failed or
 *    cancelled), and it tests that itself: before any home is read, and again
 *    inside the write (since 13 Oct 2026; functions/scrapes/matchSummary.js).
 *    A scrape that has not finished is not counted, whatever is sent, and is
 *    named in the answer (`notFinished`). Until then it counted a scrape
 *    WHATEVER ITS STATUS, and that a running scrape was never sent was this
 *    hook's duty alone. This hook still does not send one: it is no longer the
 *    only guard;
 *  - ⚠️ it does NOT test that a scrape just cancelled has stopped saving (its
 *    scraper goes on for a minute or two, and the status says "cancelled"
 *    throughout). Waiting for the scraper to be still is THIS HOOK'S, below;
 *  - it leaves a scrape that already HOLDS a count alone unless it is sent
 *    `force`. A stopped scrape holding the count of an earlier run is asked for
 *    with `force`, in a call of its own (`force` counts every scrape it is sent).
 * This hook reads nothing of the answer: a count arrives through the scrape
 * list's own listener, and a scrape the function would not count is one this
 * hook no longer needs a count for (it is running again).
 *
 * ⚠️ A SCRAPE IS LOOKED AT AGAIN JUST BEFORE ITS CALL IS SENT. The scrapes are
 * chosen when the list is drawn, and the calls go one after another, each after
 * the one before has answered: seconds as a rule, longer on the first load after
 * a publish. In that wait a scrape can be run again from its own page (Try
 * Again). So before each call the scrapes of that moment are read, and only
 * those that are STILL the same run, still need that same kind of ask, and are
 * still ready, are sent. The first version of this chose at the draw and sent
 * what it had chosen: a call could reach a scrape that was by then running
 * (the second review of PR #136, finding 3).
 *
 * ⚠️ A stopped scrape is asked about only when its scraper has been STILL for
 * three minutes (`readyToCount`): a scraper goes on saving, and writing its
 * heartbeat every ten seconds, for a minute or two after a cancel. Nothing
 * redraws the list once the box has stopped, so the page's timer (useTicker,
 * about every ten seconds, and only while something is waiting) has it looked
 * at again.
 *
 * BOUNDS. One ask of each kind per scrape per RUN; at most three asks about
 * one scrape in one page load, whatever comes back. A call that FAILS is
 * forgotten, so the scrape is asked about again at the next look, within that
 * bound of three.
 *
 * WHAT IT SAYS OF A SCRAPE IT WILL NOT ASK ABOUT AGAIN: `stuckIds`. A scrape
 * that still needs a count when its asks are spent (three made; or asked for
 * this run, answered, and thirty seconds on no count that stands has arrived)
 * will not get one until the page is loaded again. The rows must say so, and
 * not "counting" (finding 4): the ids are handed back for them.
 * ⚠️ A scrape whose three asks are SPENT is stuck AT ONCE when it stops again,
 * whether or not its scraper is yet still: nothing will ask about it, so there
 * is nothing to wait for. Said only once it was still, it was never said on a
 * quiet page: the timer does not run for a scrape that cannot be chosen, and
 * nothing else drew the list again (the review of the third commit, finding A).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { needsCount, needsCountAgain, countAskKey, readyToCount, MAX_ASKS_PER_SCRAPE, ARRIVAL_GRACE_MS } from "../utils/groupTargets";
import { useTicker } from "./useTicker";

const BATCH = 25; // the callable's own cap

export function useScrapeMatchSummaries(scrapes) {
  // ask keys (countAskKey) that have been SENT, and when each was answered (null while its call is out)
  const asked = useRef(new Map());
  // ask keys chosen at a draw and not yet sent or let go: not chosen a second time meanwhile
  const queued = useRef(new Set());
  const timesAsked = useRef(new Map());
  // the scrapes as they are now, for the look just before a call is sent
  const latest = useRef([]);
  latest.current = scrapes || [];
  const [pending, setPending] = useState(false);
  const [, setLooks] = useState(0);

  const nowMs = Date.now();
  const spent = (s) => (timesAsked.current.get(s.id) || 0) >= MAX_ASKS_PER_SCRAPE;
  const needing = latest.current.filter((s) => needsCount(s));
  const wanting = needing.filter((s) => !asked.current.has(countAskKey(s)) && !queued.current.has(countAskKey(s)) && !spent(s));
  const missing = wanting.filter((s) => readyToCount(s, nowMs));

  // Scrapes that need a count and will NOT be asked about again in this page load.
  const answeredAt = (s) => asked.current.get(countAskKey(s));
  const stuck = needing.filter((s) => {
    if (queued.current.has(countAskKey(s))) return false;
    const at = answeredAt(s);
    if (at === null) return false;                                   // its call is out
    // never asked for this run, and no asks left: it will not be asked, still or not. Stuck at once.
    if (at === undefined) return spent(s);
    // asked for this run, answered, and no count that stands has come. (It was ready when it was sent, and this is
    // the same run thirty seconds later by the same clock: there is no "still ready?" to ask again here.)
    return nowMs - at >= ARRIVAL_GRACE_MS;
  });
  // answered, and its count is still on its way: looked at again when the grace is up
  const arriving = needing.some((s) => { const at = answeredAt(s); return typeof at === "number" && nowMs - at < ARRIVAL_GRACE_MS; });

  // Some scrape is waiting (for its scraper to be still, or for its count to arrive): have the list looked at again.
  // ⚠️ The timer only REDRAWS; the time is read from the clock above at each draw, never from the timer's own value.
  useTicker(wanting.length > missing.length || arriving);

  const key = missing.map((s) => s.id).join(",");
  // the ask keys of the same scrapes, in the same order: what each was chosen AS
  const askKeys = missing.map(countAskKey).join(",");
  // those of them that hold a count which does not stand: the function must be told to count them again
  const again = missing.filter(needsCountAgain).map((s) => s.id).join(",");

  useEffect(() => {
    if (!key) return;
    const ids = key.split(",");
    const keys = askKeys.split(",");
    const chosen = ids.map((id, i) => ({ id, key: keys[i] }));
    chosen.forEach((c) => queued.current.add(c.key));
    const call = httpsCallable(functions, "summariseScrapesCallable");
    setPending(true);
    const redo = new Set(again ? again.split(",") : []);
    // two kinds of ask, never mixed in one call: `force` counts EVERY scrape it is sent again
    const asks = [
      { items: chosen.filter((c) => !redo.has(c.id)), force: false },
      { items: chosen.filter((c) => redo.has(c.id)), force: true },
    ];
    (async () => {
      for (const ask of asks) {
        for (let i = 0; i < ask.items.length; i += BATCH) {
          // ⚠️ LOOK AGAIN, now: only a scrape that is still the same run, still needs this kind of ask and is still ready
          const byId = new Map(latest.current.map((s) => [s.id, s]));
          const sendNow = [];
          for (const item of ask.items.slice(i, i + BATCH)) {
            queued.current.delete(item.key);
            const s = byId.get(item.id);
            if (!s || countAskKey(s) !== item.key || !readyToCount(s, Date.now()) || needsCountAgain(s) !== ask.force) continue;
            if ((timesAsked.current.get(item.id) || 0) >= MAX_ASKS_PER_SCRAPE) continue;
            asked.current.set(item.key, null);
            timesAsked.current.set(item.id, (timesAsked.current.get(item.id) || 0) + 1);
            sendNow.push(item);
          }
          if (sendNow.length === 0) continue;
          const scrapeIds = sendNow.map((item) => item.id);
          try {
            await call(ask.force ? { scrapeIds, force: true } : { scrapeIds });
            const at = Date.now();
            sendNow.forEach((item) => asked.current.set(item.key, at));
          } catch (e) {
            console.warn("[useScrapeMatchSummaries] could not summarise", scrapeIds, e);
            // forgotten: asked about again at the next look, within the bound of three
            sendNow.forEach((item) => asked.current.delete(item.key));
          }
        }
      }
      setPending(false);
      // whatever was let go, or failed, is chosen afresh; and the rows are told what is stuck
      setLooks((n) => n + 1);
    })();
  }, [key, askKeys, again]);

  const stuckKey = stuck.map((s) => s.id).sort().join(",");
  const stuckIds = useMemo(() => new Set(stuckKey ? stuckKey.split(",") : []), [stuckKey]);
  return { pending, stuckIds };
}

export default useScrapeMatchSummaries;
